import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Loader2, Send, PackageCheck, Ban, Trash2, ChevronDown, X as XIcon, FileText } from "lucide-react";
import ItemCombobox from "../components/ItemCombobox";
import { PrintButton, PrintHeader } from "../components/Print";
import { formatDate } from "../lib/format";

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700" },
  sent: { label: "Sent", cls: "bg-blue-100 text-blue-800" },
  received: { label: "Received", cls: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-700 line-through" },
};

function NewPODialog({ open, onOpenChange, vendors, items, onSuccess, prefillVendorId = "" }) {
  const [vendorId, setVendorId] = useState(prefillVendorId);
  const [notes, setNotes] = useState("");
  const [expectedAt, setExpectedAt] = useState("");
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setVendorId(prefillVendorId || "");
      setNotes("");
      setExpectedAt("");
      setLines([{ item_id: "", quantity: 1, unit_cost: 0 }]);
    }
  }, [open, prefillVendorId]);

  const updateLine = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));
  const addLine = () => setLines((ls) => [...ls, { item_id: "", quantity: 1, unit_cost: 0 }]);

  const subtotal = lines.reduce((s, l) => s + Number(l.unit_cost || 0) * Number(l.quantity || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!vendorId) return toast.error("Pick a vendor");
    const validLines = lines.filter((l) => l.item_id && Number(l.quantity) > 0);
    if (validLines.length === 0) return toast.error("Add at least one item with a quantity");
    setBusy(true);
    try {
      await api.post("/purchase-orders", {
        vendor_id: vendorId,
        notes,
        expected_at: expectedAt || null,
        lines: validLines.map((l) => ({
          item_id: l.item_id,
          quantity: Number(l.quantity),
          unit_cost: Number(l.unit_cost || 0),
        })),
      });
      toast.success("Purchase order created");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-3xl" data-testid="new-po-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">New Purchase Order</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Vendor</Label>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger data-testid="po-vendor-select" className="rounded-sm mt-1.5">
                  <SelectValue placeholder="Select vendor…" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Expected date</Label>
              <Input type="date" value={expectedAt} onChange={(e) => setExpectedAt(e.target.value)} data-testid="po-expected-input" className="rounded-sm mt-1.5" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Line items</Label>
              <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="po-add-line-btn" className="rounded-sm">
                <Plus size={14} className="mr-1" /> Add line
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((l, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start border border-slate-200 rounded-sm p-2 bg-slate-50">
                  <div className="col-span-6">
                    <ItemCombobox items={items} value={l.item_id} onChange={(v) => updateLine(idx, { item_id: v })} testId={`po-line-item-${idx}`} placeholder="Item…" />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" min={1} value={l.quantity} onChange={(e) => updateLine(idx, { quantity: e.target.value })} data-testid={`po-line-qty-${idx}`} className="rounded-sm" placeholder="Qty" />
                  </div>
                  <div className="col-span-3">
                    <Input type="number" min={0} step="0.01" value={l.unit_cost} onChange={(e) => updateLine(idx, { unit_cost: e.target.value })} data-testid={`po-line-cost-${idx}`} className="rounded-sm" placeholder="Unit cost" />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {lines.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" onClick={() => removeLine(idx)} data-testid={`po-line-remove-${idx}`} className="h-8 w-8 text-slate-500 hover:text-red-600">
                        <XIcon size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-end text-sm">
              <div className="font-mono font-semibold text-slate-900">
                Subtotal: ${subtotal.toFixed(2)}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="po-notes-input" className="rounded-sm mt-1.5" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="po-submit-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
              Create as draft
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function POCard({ po, onSend, onReceive, onCancel, onDelete, canDelete }) {
  const [open, setOpen] = useState(false);
  const meta = STATUS_META[po.status] || STATUS_META.draft;
  return (
    <div className="border border-slate-200 rounded-md bg-white shadow-sm overflow-hidden" data-testid={`po-card-${po.id}`}>
      <div className="flex items-start justify-between gap-4 p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display text-lg font-black tracking-tight text-slate-900">{po.po_number}</span>
            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm ${meta.cls}`}>
              {meta.label}
            </span>
          </div>
          <div className="mt-1 text-sm text-slate-700 font-semibold">{po.vendor_name}</div>
          <div className="mt-1 text-xs font-mono text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
            <span>{po.line_count} line{po.line_count === 1 ? "" : "s"}</span>
            <span>·</span>
            <span>{po.total_quantity} units</span>
            <span>·</span>
            <span>${po.subtotal.toFixed(2)}</span>
            {po.expected_at && (<><span>·</span><span>Expected {po.expected_at}</span></>)}
          </div>
          {po.notes && <div className="mt-2 text-xs text-slate-600 italic">"{po.notes}"</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {po.status === "draft" && (
            <Button size="sm" onClick={() => onSend(po)} data-testid={`po-send-${po.id}`} className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              <Send size={14} className="mr-1.5" /> Mark sent
            </Button>
          )}
          {(po.status === "sent" || po.status === "draft") && (
            <Button size="sm" variant="outline" onClick={() => onReceive(po)} data-testid={`po-receive-${po.id}`} className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <PackageCheck size={14} className="mr-1.5" /> Receive
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="PO actions" data-testid={`po-actions-${po.id}`} className="h-8 w-8 rounded-sm hover:bg-slate-100">
                <MoreHorizontal size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-sm">
              <DropdownMenuLabel className="text-xs uppercase tracking-widest text-slate-500">Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setOpen((o) => !o)} data-testid={`po-toggle-${po.id}`}>
                <FileText size={14} className="mr-2" /> {open ? "Hide" : "Show"} details
              </DropdownMenuItem>
              {po.status !== "received" && po.status !== "cancelled" && (
                <DropdownMenuItem onClick={() => onCancel(po)} data-testid={`po-cancel-${po.id}`}>
                  <Ban size={14} className="mr-2" /> Cancel PO
                </DropdownMenuItem>
              )}
              {canDelete && po.status !== "received" && (
                <DropdownMenuItem onClick={() => onDelete(po)} className="text-red-600 focus:text-red-700 focus:bg-red-50" data-testid={`po-delete-${po.id}`}>
                  <Trash2 size={14} className="mr-2" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" onClick={() => setOpen((o) => !o)} className="h-8 w-8" aria-label="Expand">
            <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </Button>
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="py-2">Item</th>
                <th className="py-2">SKU</th>
                <th className="py-2 text-right">Qty</th>
                <th className="py-2 text-right">Unit</th>
                <th className="py-2 text-right">Line total</th>
              </tr>
            </thead>
            <tbody>
              {po.lines.map((l, i) => (
                <tr key={i} className="border-t border-slate-200">
                  <td className="py-2 text-slate-900">{l.item_name}</td>
                  <td className="py-2 font-mono text-xs text-slate-500">{l.item_sku}</td>
                  <td className="py-2 text-right font-mono">{l.quantity}</td>
                  <td className="py-2 text-right font-mono">${Number(l.unit_cost || 0).toFixed(2)}</td>
                  <td className="py-2 text-right font-mono font-semibold">${(Number(l.unit_cost || 0) * Number(l.quantity)).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 text-xs font-mono text-slate-500 flex flex-wrap gap-x-4">
            <span>Created {formatDate(po.created_at)} by {po.created_by_name}</span>
            {po.sent_at && <span>· Sent {formatDate(po.sent_at)}</span>}
            {po.received_at && <span>· Received {formatDate(po.received_at)}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PurchaseOrdersPage() {
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tab, setTab] = useState("open"); // open | received | cancelled
  const { canDelete } = useAuth();

  const load = async () => {
    const [{ data: p }, { data: v }, { data: i }] = await Promise.all([
      api.get("/purchase-orders"),
      api.get("/vendors"),
      api.get("/inventory/items"),
    ]);
    setPos(p);
    setVendors(v);
    setItems(i);
  };
  useEffect(() => { load(); }, []);

  const filtered = pos.filter((p) => {
    if (tab === "open") return p.status === "draft" || p.status === "sent";
    if (tab === "received") return p.status === "received";
    return p.status === "cancelled";
  });

  const doSend = async (po) => {
    try {
      await api.post(`/purchase-orders/${po.id}/send`);
      toast.success(`${po.po_number} marked sent`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
  };
  const doReceive = async (po) => {
    if (!window.confirm(`Receive ${po.po_number} — this will add ${po.total_quantity} units to warehouse stock.`)) return;
    try {
      await api.post(`/purchase-orders/${po.id}/receive`);
      toast.success(`Received ${po.po_number}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Receive failed"); }
  };
  const doCancel = async (po) => {
    if (!window.confirm(`Cancel ${po.po_number}?`)) return;
    try {
      await api.post(`/purchase-orders/${po.id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Cancel failed"); }
  };
  const doDelete = async (po) => {
    if (!window.confirm(`Permanently delete ${po.po_number}?`)) return;
    try {
      await api.delete(`/purchase-orders/${po.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const counts = {
    open: pos.filter((p) => p.status === "draft" || p.status === "sent").length,
    received: pos.filter((p) => p.status === "received").length,
    cancelled: pos.filter((p) => p.status === "cancelled").length,
  };

  return (
    <div>
      <PageHeader
        eyebrow="Supply chain"
        title="Purchase Orders"
        description="Draft, send, and receive orders — every received PO logs a receive transaction automatically."
        testId="pos-header"
        actions={
          <div className="flex items-center gap-2 no-print">
            <PrintButton testId="pos-print-btn" />
            <Button
              onClick={() => setDialogOpen(true)}
              data-testid="new-po-btn"
              className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
              disabled={vendors.length === 0}
              title={vendors.length === 0 ? "Add a vendor first" : "Create purchase order"}
            >
              <Plus size={16} className="mr-1.5" /> New PO
            </Button>
          </div>
        }
      />

      <div className="px-4 md:px-8 pt-6 print-container">
        <PrintHeader title="Purchase Orders" subtitle={`${pos.length} total`} />
        {vendors.length === 0 && (
          <div className="border border-amber-200 bg-amber-50 rounded-md p-4 text-sm text-amber-900 mb-4">
            You haven't added any vendors yet. <a href="/vendors" className="font-semibold underline">Add your first vendor →</a>
          </div>
        )}
        <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden no-print mb-6" data-testid="po-status-switch">
          {(["open", "received", "cancelled"]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              data-testid={`po-tab-${k}`}
              className={`px-4 py-2 text-sm font-semibold transition-colors border-l first:border-l-0 border-slate-300 ${tab === k ? "bg-blue-800 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            >
              {k[0].toUpperCase() + k.slice(1)}
              <span className={`ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${tab === k ? "bg-blue-900 text-blue-100" : "bg-slate-100 text-slate-600"}`}>{counts[k]}</span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="border border-dashed border-slate-300 bg-white rounded-md p-12 text-center">
            <FileText size={40} className="mx-auto text-slate-400" />
            <div className="mt-3 font-display text-xl font-bold text-slate-900">No {tab} purchase orders</div>
            <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">
              {tab === "open"
                ? "Start a draft PO to lock in an order with a vendor."
                : `Nothing in the "${tab}" bucket yet.`}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((po) => (
              <POCard key={po.id} po={po} onSend={doSend} onReceive={doReceive} onCancel={doCancel} onDelete={doDelete} canDelete={canDelete} />
            ))}
          </div>
        )}
      </div>

      <NewPODialog open={dialogOpen} onOpenChange={setDialogOpen} vendors={vendors} items={items} onSuccess={load} />
    </div>
  );
}
